/**
 * SelectorPropiedad.jsx
 *
 * Shown after login when the authenticated user has access to multiple
 * properties. Displays a card grid so the user can pick which property
 * to work in. Clicking a card stores the selection in localStorage and
 * navigates to /dashboard.
 *
 * Usage (App.jsx):
 *   <Route path="/seleccionar-propiedad" element={<SelectorPropiedad />} />
 *
 * The Login flow should redirect here when the user's token is valid but
 * no propiedad_id is stored in localStorage yet.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Building2, MapPin, Shield, LogOut, Loader, AlertTriangle, Hotel } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { MIS_PROPIEDADES } from '../graphql/propiedades';
import LogoLight from '../assets/logo-posfac-dark.png';
import LogoDark from '../assets/logo-posfac.png';
import './SelectorPropiedad.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const rolLabels = {
  superadmin: 'Super Administrador',
  admin: 'Administrador',
  gerente: 'Gerente',
  recepcionista: 'Recepcionista',
  limpieza: 'Limpieza',
  mantenimiento: 'Mantenimiento',
};

/**
 * Returns the first 1–2 uppercase characters of a property name to use as
 * fallback avatar when no logo_url is available.
 */
function getInitials(nombre = '') {
  const words = nombre.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Generates a stable background gradient from a hex color for the avatar
 * when the property has a color_primario but no logo.
 */
function buildAvatarGradient(colorPrimario) {
  const base = colorPrimario || '#1e40af';
  // Darken slightly for the gradient end stop
  return `linear-gradient(135deg, ${base}cc 0%, ${base} 100%)`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SelectorPropiedad() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const { data, loading, error } = useQuery(MIS_PROPIEDADES, {
    fetchPolicy: 'cache-and-network',
  });

  const propiedades = data?.misPropiedades ?? [];

  // Auto-redirect when user only has one property
  useEffect(() => {
    if (propiedades.length === 1) {
      const unica = propiedades[0];
      localStorage.setItem('propiedad_id', String(unica.id));
      localStorage.setItem('propiedad_nombre', unica.nombre);
      navigate('/dashboard', { replace: true });
    }
  }, [propiedades, navigate]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSeleccionar(propiedad) {
    localStorage.setItem('propiedad_id', String(propiedad.id));
    localStorage.setItem('propiedad_nombre', propiedad.nombre);
    navigate('/dashboard');
  }

  function handleCerrarSesion() {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem('propiedad_id');
    localStorage.removeItem('propiedad_nombre');
    navigate('/login', { replace: true });
  }

  // ── Render states ─────────────────────────────────────────────────────────

  const logoSrc = isDark ? LogoDark : LogoLight;

  const header = (
    <header className="sp__header">
      <img
        src={logoSrc}
        alt="Posfac Hotel"
        className="sp__header-logo"
      />
      <h1 className="sp__header-title">Seleccionar Propiedad</h1>
      <button
        className="sp__btn-logout"
        onClick={handleCerrarSesion}
        aria-label="Cerrar sesión"
        type="button"
      >
        <LogOut size={16} aria-hidden="true" />
        <span>Cerrar Sesión</span>
      </button>
    </header>
  );

  if (loading) {
    return (
      <div className="sp__page" aria-busy="true" aria-label="Cargando propiedades">
        {header}
        <main className="sp__main">
          <div className="sp__state-wrapper">
            <Loader size={40} className="sp__spinner" aria-hidden="true" />
            <p className="sp__state-text">Cargando tus propiedades...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sp__page" role="alert">
        {header}
        <main className="sp__main">
          <div className="sp__state-wrapper">
            <AlertTriangle size={40} className="sp__state-icon sp__state-icon--error" aria-hidden="true" />
            <p className="sp__state-text sp__state-text--error">
              Error al cargar las propiedades
            </p>
            <p className="sp__state-subtext">{error.message}</p>
            <button
              className="sp__btn-retry"
              onClick={() => window.location.reload()}
              type="button"
            >
              Reintentar
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (propiedades.length === 0) {
    return (
      <div className="sp__page">
        {header}
        <main className="sp__main">
          <div className="sp__state-wrapper">
            <Hotel size={40} className="sp__state-icon sp__state-icon--empty" aria-hidden="true" />
            <p className="sp__state-text">Sin propiedades asignadas</p>
            <p className="sp__state-subtext">
              Tu cuenta no tiene acceso a ninguna propiedad. Contacta al administrador del sistema.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="sp__page">
      {header}

      <main className="sp__main">
        <div className="sp__intro">
          <p className="sp__intro-text">
            Selecciona la propiedad en la que deseas trabajar
          </p>
        </div>

        <ul
          className="sp__grid"
          role="list"
          aria-label="Lista de propiedades disponibles"
        >
          {propiedades.map((propiedad) => {
            const accentColor = propiedad.color_primario || '#1e40af';
            const rolLabel = rolLabels[propiedad.rol_usuario] ?? propiedad.rol_usuario ?? 'Usuario';

            return (
              <li key={propiedad.id} role="listitem">
                <button
                  className="sp__card"
                  onClick={() => handleSeleccionar(propiedad)}
                  type="button"
                  aria-label={`Seleccionar ${propiedad.nombre}`}
                  style={{ '--sp-accent': accentColor }}
                >
                  {/* Top accent bar */}
                  <span className="sp__card-accent-bar" aria-hidden="true" />

                  {/* Avatar / logo */}
                  <div className="sp__card-avatar-wrap" aria-hidden="true">
                    {propiedad.logo_url ? (
                      <img
                        src={propiedad.logo_url}
                        alt=""
                        className="sp__card-logo"
                      />
                    ) : (
                      <div
                        className="sp__card-initials"
                        style={{ background: buildAvatarGradient(propiedad.color_primario) }}
                      >
                        {getInitials(propiedad.nombre)}
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div className="sp__card-body">
                    <h2 className="sp__card-name">{propiedad.nombre}</h2>

                    {propiedad.ciudad && (
                      <p className="sp__card-meta">
                        <MapPin size={13} aria-hidden="true" />
                        <span>{propiedad.ciudad}{propiedad.departamento ? `, ${propiedad.departamento}` : ''}</span>
                      </p>
                    )}

                    <p className="sp__card-meta">
                      <Building2 size={13} aria-hidden="true" />
                      <span>{propiedad.nombre_corto ?? propiedad.nombre}</span>
                    </p>

                    <div className="sp__card-role">
                      <Shield size={12} aria-hidden="true" />
                      <span>{rolLabel}</span>
                    </div>
                  </div>

                  {/* Hover arrow */}
                  <span className="sp__card-arrow" aria-hidden="true">→</span>
                </button>
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}
